import { CampaignAnalyticsReport } from '@components/Dashboard/CampaignAnalytics/slice/schema';
import { themeV2 } from '@features/executive-report/domain/theme-v2';
import { audienceProfilePairsByGroup } from '@features/executive-report/subdomains/visitation/slides/audience-profile';
import { slideMasters } from '@features/executive-report/utils/slide-masters';
import { ShapeProps, Slide, TextProps } from '@features/powerpoint';
import { cardBackground } from './components/card-background';

const TOP_AUDIENCE_LABEL_X_START = 0.56;
const TOP_AUDIENCE_LABEL_Y_START = 2.1;
const TOP_AUDIENCE_X_OFFSET = 3.21;
const TOP_AUDIENCE_Y_OFFSET = 2.21;

const EXTRA_AUDIENCE_X_START = 7.52;
const EXTRA_AUDIENCE_Y_START = 2.33;
const EXTRA_AUDIENCE_Y_OFFSET = 0.58;

type SlideProps = {
  audienceProfile: CampaignAnalyticsReport['audienceProfile'];
};

export const audienceProfileSlide = (props: SlideProps): Slide => {
  const { top, extra } = audienceProfilePairsByGroup(props.audienceProfile);

  return {
    slideMaster: slideMasters.light,
    color: themeV2.colors.metricBlue,
    shapes: [
      ...top.map((_, index) =>
        cardBackground({
          h: 2.1,
          w: 3.08,
          x: 0.44 + (index % 2) * TOP_AUDIENCE_X_OFFSET,
          y: 1.77 + Math.floor(index / 2) * TOP_AUDIENCE_Y_OFFSET,
        })
      ),
      ...extra.map(
        (_, index): ShapeProps => ({
          shapeName: 'roundRect',
          options: {
            fill: {
              type: 'solid',
              color: themeV2.colors.lightGray,
              transparency: 25,
            },
            h: 0.43,
            w: 4.67,
            x: EXTRA_AUDIENCE_X_START,
            y: EXTRA_AUDIENCE_Y_START + index * EXTRA_AUDIENCE_Y_OFFSET,
          },
        })
      ),
    ],
    texts: [
      {
        text: 'Audience Profile (Saw the Ad & Visited)',
        options: {
          fontSize: 36,
          bold: true,
          h: 0.75,
          w: 12.67,
          x: 0.33,
          y: 0.25,
        },
      },
      ...top.flatMap((nameValuePair, index): TextProps[] => {
        return [
          {
            text: nameValuePair.name.toLocaleUpperCase(),
            options: {
              fontSize: 10,
              bold: true,
              color: themeV2.colors.inmarketGreen,
              align: 'center',
              h: 0.29,
              w: 2.84,
              x:
                TOP_AUDIENCE_LABEL_X_START +
                (index % 2) * TOP_AUDIENCE_X_OFFSET,
              y:
                TOP_AUDIENCE_LABEL_Y_START +
                Math.floor(index / 2) * TOP_AUDIENCE_Y_OFFSET,
            },
          },
          {
            text: nameValuePair.value.toString(),
            options: {
              fontSize: 40,
              bold: true,
              color:
                index / 2 >= 1
                  ? themeV2.colors.metricBlue
                  : themeV2.colors.inmarketGreen,
              align: 'center',
              h: 0.87,
              w: 3.08,
              x: 0.44 + (index % 2) * TOP_AUDIENCE_X_OFFSET,
              y: 2.4 + Math.floor(index / 2) * TOP_AUDIENCE_Y_OFFSET,
            },
          },
        ];
      }),
      ...extra.flatMap((nameValuePair, index): TextProps[] => {
        return [
          {
            text: nameValuePair.value.toString(),
            options: {
              fontSize: 18,
              color: themeV2.colors.inmarketGreen,
              bold: true,
              align: 'center',
              h: 0.34,
              w: 0.63,
              x: EXTRA_AUDIENCE_X_START,
              y:
                EXTRA_AUDIENCE_Y_START + index * EXTRA_AUDIENCE_Y_OFFSET + 0.04,
            },
          },
          {
            text: nameValuePair.name,
            options: {
              fontSize: 11,
              h: 0.23,
              w: 4.02,
              x: EXTRA_AUDIENCE_X_START + 0.58,
              y: EXTRA_AUDIENCE_Y_START + index * EXTRA_AUDIENCE_Y_OFFSET + 0.1,
            },
          },
        ];
      }),
    ],
  };
};
